import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Responsive as ResponsiveGridLayout, Layouts } from 'react-grid-layout';
import './panelContents.scss';
import { getPanelLayouts } from '@/constants/panelLayouts';
import { captureException } from '../../utils/sentry';
import { useUser } from '@/hooks/UserContext';
import { setLocalStorage, getLocalStorage } from '@/utils/localStrage';
import Loading from '@/components/Loading';
import PanelLoadingBoundary from '@/components/PanelLoadingBoundary';
import { FullscreenLockedModal } from '@/components/FullscreenLockedModal';
import { usePanelFullscreen } from './usePanelFullscreen';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import {
    createFullscreenPortal,
    getPanelRenderInfo,
    FullscreenToggleButton,
} from './panelFullscreenHelpers';

// 定数定義
const STORAGE_KEY = 'panel_layout_info';
const BREAKPOINTS = {
    PC: 1024,
    TABLET: 650,
    MOBILE: 0,
} as const;

const OVERFLOW_HIDDEN_PANEL_IDS = new Set(['hmMemo']);
const FULLSCREEN_ELIGIBLE_PANEL_IDS = new Set([
    'hmTimeClock',
    'hmActivityCalendar',
    'hmSimpleAnalytics',
    'hmMemo',
]);
const FULLSCREEN_MENU_PANEL_IDS = new Set([
    'hmTimeClock',
    'hmActivityCalendar',
    'hmSimpleAnalytics',
]);

/**
 * グリッドレイアウトの設定
 */
const GRID_LAYOUT_CONFIG = {
    default: {
        breakpoints: { pc: BREAKPOINTS.PC, tablet: BREAKPOINTS.TABLET, mobile: BREAKPOINTS.MOBILE },
        cols: { pc: 12, tablet: 6, mobile: 2 },
        rowHeight: 80,
        containerPadding: [24, 24] as [number, number],
        margin: [24, 24] as [number, number],
        preventCollision: false,
        compactType: 'vertical' as 'vertical' | 'horizontal' | null,
        useCSSTransforms: false,
        draggableHandle: '.panel-drag-handle',
    },
};

type Breakpoint = 'pc' | 'tablet' | 'mobile';

/**
 * デフォルトのレイアウト設定に、保存された位置情報をマージする
 */
function mergeSavedIntoDefault(defaultLayouts: Layouts, allSavedLayouts: Layouts): Layouts {
    const result: Layouts = { ...defaultLayouts };
    (Object.keys(result) as Array<keyof Layouts>).forEach((bp) => {
        const savedBpLayout = allSavedLayouts[bp];
        if (!savedBpLayout) return;

        const savedItemsMap = new Map(savedBpLayout.map((item: any) => [item.i, item]));

        result[bp] = defaultLayouts[bp].map((defaultItem) => {
            const savedItem = savedItemsMap.get(defaultItem.i);
            return savedItem ? { ...defaultItem, ...savedItem } : defaultItem;
        });
    });
    return result;
}

/**
 * グリッド幅に基づいてブレークポイントを計算
 */
function calculateBreakpoint(width: number): Breakpoint {
    if (width >= BREAKPOINTS.PC) return 'pc';
    if (width >= BREAKPOINTS.TABLET) return 'tablet';
    return 'mobile';
}

/**
 * パネルコンテンツ
 */
function PanelContents({
    componentMap,
    panelsOverlayLoading = false,
    panelsOverlayLabel,
}: {
    componentMap: Record<string, React.ReactNode>;
    /** 各パネルに同じローディングオーバーレイをかける（分析の追加取得など） */
    panelsOverlayLoading?: boolean;
    panelsOverlayLabel?: string;
}): ReactElement {
    const { t } = useTranslation('common');
    const { userInfo } = useUser();
    const isPaidPlan = userInfo?.isPaid === true;
    const referralDeskPanelVisible = userInfo?.referralDeskPanelVisible === true;

    // 状態管理
    const [layouts, setLayouts] = useState<Layouts | null>(null);
    const [currentBreakpoint, setCurrentBreakpoint] = useState<Breakpoint>('pc');
    const [gridWidth, setGridWidth] = useState(0);
    const [isFullscreenLockedModalOpen, setIsFullscreenLockedModalOpen] = useState(false);
    const [lockedPanelId, setLockedPanelId] = useState<string | null>(null);

    // フルスクリーン機能の設定
    const fullscreenEligibleIds = isPaidPlan ? FULLSCREEN_ELIGIBLE_PANEL_IDS : new Set<string>();
    const {
        fullScreenPanelId,
        enterFullScreen: handleEnterFullScreen,
        exitFullScreen: handleExitFullScreen,
    } = usePanelFullscreen(fullscreenEligibleIds);

    // 現在のブレークポイントを計算
    const effectiveBreakpoint = calculateBreakpoint(gridWidth);

    // レイアウトの初期化・読み込み
    useEffect(() => {
        const initLayouts = async () => {
            if (!userInfo) return;

            // デフォルトレイアウトを取得
            const fullDefaultLayouts = getPanelLayouts(isPaidPlan, referralDeskPanelVisible);

            // このページで表示すべきパネルだけを抽出
            const pageSpecificDefaultLayouts: Layouts = {};
            (Object.keys(fullDefaultLayouts) as Array<keyof typeof fullDefaultLayouts>).forEach(
                (bp) => {
                    pageSpecificDefaultLayouts[bp] = fullDefaultLayouts[bp].filter((item: { i: string }) =>
                        Object.prototype.hasOwnProperty.call(componentMap, item.i),
                    );
                },
            );

            let finalLayouts = pageSpecificDefaultLayouts;

            // 保存済みのレイアウトを読み込んでマージ
            if (userInfo.userId) {
                const savedStr = await getLocalStorage(userInfo.userId, STORAGE_KEY);
                if (savedStr) {
                    try {
                        const allSavedLayouts = JSON.parse(savedStr);
                        finalLayouts = mergeSavedIntoDefault(pageSpecificDefaultLayouts, allSavedLayouts);
                    } catch (e) {
                        captureException(e as Error, {
                            componentName: 'PanelContents',
                            action: 'initLayouts',
                            message: 'Failed to parse saved layout',
                        });
                    }
                }
            }

            setLayouts(finalLayouts);
        };

        initLayouts();
    }, [isPaidPlan, referralDeskPanelVisible, componentMap, userInfo]);

    // グリッド幅が更新されたときにブレークポイントを更新
    useEffect(() => {
        if (gridWidth > 0) {
            const breakpoint = calculateBreakpoint(gridWidth);
            setCurrentBreakpoint(breakpoint);
        }
    }, [gridWidth]);

    // メインコンテンツの幅を監視
    useEffect(() => {
        const mainContent = document.querySelector('main');
        if (!mainContent) return;

        const updateWidth = () => {
            const newWidth = mainContent.clientWidth;
            setGridWidth(newWidth);
        };

        updateWidth();

        window.addEventListener('resize', updateWidth);
        const resizeObserver = new ResizeObserver(updateWidth);
        resizeObserver.observe(mainContent);

        return () => {
            window.removeEventListener('resize', updateWidth);
            resizeObserver.disconnect();
        };
    }, []);

    // グリッドアイテムのスタイルを適用
    useEffect(() => {
        if (!layouts || !currentBreakpoint) return;

        const currentLayouts = layouts[currentBreakpoint] || [];

        const applyGridItemStyles = () => {
            currentLayouts.forEach((layout) => {
                const gridItem = document.querySelector(
                    `.react-grid-item[data-grid-key="${layout.i}"]`,
                );
                if (!gridItem) return;

                // static属性の適用
                if (layout.static) {
                    gridItem.classList.add('static');
                } else {
                    gridItem.classList.remove('static');
                }

                // ドラッグ可否の適用
                if (layout.isDraggable === false) {
                    gridItem.classList.add('no-drag');
                } else {
                    gridItem.classList.remove('no-drag');
                }

                // リサイズ可否の適用
                if (layout.isResizable === false) {
                    gridItem.classList.add('no-resize');
                } else {
                    gridItem.classList.remove('no-resize');
                }
            });
        };

        setTimeout(applyGridItemStyles, 0);
    }, [layouts, currentBreakpoint]);

    // レイアウト変更の保存
    const handleLayoutChange = async (_currentLayout: any[], allChangedLayouts: Layouts) => {
        if (!userInfo?.userId) return;

        try {
            // UIに即時反映
            setLayouts((prev) => (prev ? { ...prev, ...allChangedLayouts } : null));

            // 保存済みレイアウトを取得
            const savedStr = await getLocalStorage(userInfo.userId, STORAGE_KEY);
            const fullSavedLayouts = savedStr
                ? JSON.parse(savedStr)
                : getPanelLayouts(isPaidPlan, referralDeskPanelVisible);

            const currentPagePanelKeys = Object.keys(componentMap);

            // 変更があったブレークポイントのデータをマージ
            for (const bp of Object.keys(allChangedLayouts)) {
                const key = bp as keyof Layouts;
                const changedItemsForBp = allChangedLayouts[key];

                if (!changedItemsForBp || changedItemsForBp.length === 0) continue;

                const allItemsInStorageForBp = fullSavedLayouts[key] || [];
                const otherPagesItems = allItemsInStorageForBp.filter(
                    (item: { i: string }) => !currentPagePanelKeys.includes(item.i),
                );

                fullSavedLayouts[key] = [...otherPagesItems, ...changedItemsForBp];
            }

            // 保存用に軽量化
            const strippedLayouts = JSON.parse(JSON.stringify(fullSavedLayouts));
            for (const bp in strippedLayouts) {
                strippedLayouts[bp] = strippedLayouts[bp].map((item: any) => ({
                    i: item.i,
                    x: item.x,
                    y: item.y,
                    w: item.w,
                    h: item.h,
                }));
            }

            await setLocalStorage(userInfo.userId, STORAGE_KEY, JSON.stringify(strippedLayouts));
        } catch (e) {
            captureException(e as Error, {
                componentName: 'PanelContents',
                action: 'handleLayoutChange',
                message: 'Failed to save layout',
            });
        }
    };

    // ブレークポイント変更時のハンドラー
    const handleBreakpointChange = (_newBreakpoint: string) => {
        // react-grid-layoutのブレークポイントは無視し、gridWidthに基づくブレークポイントを使用
        const calculatedBreakpoint = calculateBreakpoint(gridWidth);
        setCurrentBreakpoint(calculatedBreakpoint);
    };

    // フルスクリーンロックモーダルのイベント監視
    useEffect(() => {
        const handler = (event: Event) => {
            const customEvent = event as CustomEvent<{ panelId?: string }>;
            setLockedPanelId(customEvent.detail?.panelId ?? null);
            setIsFullscreenLockedModalOpen(true);
        };

        window.addEventListener('kotomil:fullscreenLockedClick', handler as EventListener);
        return () => {
            window.removeEventListener('kotomil:fullscreenLockedClick', handler as EventListener);
        };
    }, []);

    // ロード中はローディング表示
    if (!layouts) {
        return <Loading />;
    }

    // レンダリング用の設定
    const fullscreenEnterLabel = t('actions.fullscreenEnter');
    const fullscreenExitLabel = t('actions.fullscreenExit');

    const fullScreenPortal = createFullscreenPortal({
        fullScreenPanelId,
        eligiblePanelIds: fullscreenEligibleIds,
        menuPanelIds: FULLSCREEN_MENU_PANEL_IDS,
        componentMap,
        fullscreenExitLabel,
        onEnterFullScreen: handleEnterFullScreen,
        onExitFullScreen: handleExitFullScreen,
    });

    const currentLayouts = layouts[effectiveBreakpoint] || [];

    return (
        <>
            <div className="relative w-full max-w-full">
                <ResponsiveGridLayout
                    {...GRID_LAYOUT_CONFIG.default}
                    layouts={layouts}
                    width={gridWidth}
                    className="custom-grid-layout"
                    onBreakpointChange={handleBreakpointChange}
                    onLayoutChange={handleLayoutChange}
                >
                    {currentLayouts.map((layout) => {
                        const panel = componentMap[layout.i];
                        if (!panel) return null;

                        const {
                            panelElement,
                            isFullScreenEnabled,
                            isFullScreen,
                            shouldRenderDefaultFullscreenButton,
                        } = getPanelRenderInfo({
                            panelId: layout.i,
                            panel,
                            fullScreenPanelId,
                            eligiblePanelIds: FULLSCREEN_ELIGIBLE_PANEL_IDS,
                            menuPanelIds: FULLSCREEN_MENU_PANEL_IDS,
                            onEnterFullScreen: handleEnterFullScreen,
                            onExitFullScreen: handleExitFullScreen,
                        });

                        return (
                            <div
                                key={layout.i}
                                data-grid-key={layout.i}
                                className={`panel-shadow border border-gray-200 rounded-lg p-1 flex flex-col justify-center items-center w-full h-full relative ${
                                    isFullScreen && isFullScreenEnabled ? 'is-fullscreen-active' : ''
                                }`}
                            >
                                {shouldRenderDefaultFullscreenButton && (
                                    <FullscreenToggleButton
                                        isFullScreen={isFullScreen}
                                        onExit={handleExitFullScreen}
                                        onEnter={() => handleEnterFullScreen(layout.i)}
                                        enterLabel={fullscreenEnterLabel}
                                        exitLabel={fullscreenExitLabel}
                                        isLocked={!isPaidPlan}
                                        onLockedClick={() => {
                                            window.dispatchEvent(
                                                new CustomEvent('kotomil:fullscreenLockedClick', {
                                                    detail: { panelId: layout.i },
                                                }),
                                            );
                                        }}
                                    />
                                )}
                                <PanelLoadingBoundary
                                    overlayLoading={panelsOverlayLoading}
                                    overlayLabel={panelsOverlayLabel}
                                    wrapperClassName={`flex-1 w-full min-h-0 ${
                                        OVERFLOW_HIDDEN_PANEL_IDS.has(layout.i) ? 'overflow-hidden' : ''
                                    }`}
                                    className="flex flex-col justify-center items-center h-full w-full"
                                >
                                    {panelElement}
                                </PanelLoadingBoundary>
                                <div className="panel-drag-handle cursor-move">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i}></div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            </div>
            {fullScreenPortal}
            {!isPaidPlan && (
                <FullscreenLockedModal
                    isOpen={isFullscreenLockedModalOpen}
                    onClose={() => setIsFullscreenLockedModalOpen(false)}
                    onGoPlans={() => {
                        setIsFullscreenLockedModalOpen(false);
                        openPlansWithTracking({
                            source: 'fullscreen_panelButton',
                            panelId: lockedPanelId ?? undefined,
                        });
                    }}
                />
            )}
        </>
    );
}

export default PanelContents;
